import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Search, Lock, Unlock, ArrowLeftRight, Users } from 'lucide-react'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'

/**
 * Alocarea clienților pe agenți.
 *
 * A apărut din plecarea unui agent: clienții lui trebuiau împărțiți altora. În ExpertAccounts
 * rămân însă pe numele lui, iar sincronizarea rescrie agentul la fiecare rulare — de aceea
 * alocarea făcută aici se „blochează" și devine autoritară. Lacătul din listă arată exact asta.
 */

interface ClientAlocare {
  id: number
  name: string
  cif: string | null
  agentName: string | null
  agentLocked: boolean
  grup: string | null
  soldLei: number | null
}

interface Duplicat {
  cif: string
  clienti: ClientAlocare[]
}

const lei = (n: number | null) =>
  n == null
    ? '—'
    : n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function AlocareClientiPage() {
  const qc = useQueryClient()
  const [agent, setAgent] = useState('')
  const [cautaAlocati, setCautaAlocati] = useState('')
  const [cautaRestul, setCautaRestul] = useState('')
  const [selectatiCeilalti, setSelectatiCeilalti] = useState<Set<number>>(new Set())
  const [selectatiAiLui, setSelectatiAiLui] = useState<Set<number>>(new Set())
  /** Agentul catre care se muta selectia din lista din stanga. */
  const [mutaLa, setMutaLa] = useState('')

  const { data: agenti } = useQuery<{ agenti: { name: string; clienti: number }[] }>({
    queryKey: ['alocare', 'agenti'],
    queryFn: async () => (await api.get('/agents/alocare/agenti')).data,
  })

  const { data, isLoading } = useQuery<{ alocati: ClientAlocare[]; restul: ClientAlocare[] }>({
    queryKey: ['alocare', 'clienti', agent],
    queryFn: async () => (await api.get('/agents/alocare/clienti', { params: { agent } })).data,
    enabled: !!agent,
  })

  /*
    Fișe dispărute din EA — acolo, unificarea mută tranzacțiile pe partenerul care rămâne și
    elimină fișa veche (Alexandru, 15.10.2026). Aici se unifică la fel, cu un clic, dar doar după
    confirmare: nimic nu se unifică singur.
  */
  const { data: unifEa } = useQuery<{
    unificate: {
      clientId: number
      denumire: string
      codFiscal: string | null
      lipsaDin: string
      propunere: {
        clientId: number
        denumire: string
        codFiscal: string | null
        motiv: string
      } | null
    }[]
  }>({
    queryKey: ['alocare', 'unificate-ea'],
    queryFn: async () => (await api.get('/agents/alocare/unificate-ea', { timeout: 90_000 })).data,
    staleTime: 10 * 60_000,
  })

  const { data: dup } = useQuery<{ duplicate: Duplicat[] }>({
    queryKey: ['alocare', 'duplicate'],
    queryFn: async () => (await api.get('/agents/alocare/duplicate')).data,
  })

  const reset = () => {
    setSelectatiCeilalti(new Set())
    setSelectatiAiLui(new Set())
    setMutaLa('')
  }

  /**
   * Mutarea directa de la un agent la altul.
   *
   * Foloseste acelasi endpoint ca restul: alocarea la un agent E mutarea, fiindca un client
   * are un singur agent. Diferenta e doar de unde privesti — de aceea nu era evident.
   */
  const muta = useMutation({
    mutationFn: async (v: { catre: string; ids: number[] }) =>
      (await api.post('/agents/alocare', { agent: v.catre, adauga: v.ids, scoate: [] })).data,
    onSuccess: (r: { adaugati: number }, v) => {
      toast.success(`${r.adaugati} clienți mutați la ${v.catre}.`)
      reset()
      void qc.invalidateQueries({ queryKey: ['alocare'] })
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message ?? 'Mutarea a eșuat.'),
  })

  /** Scoaterea de la agent, fara destinatie — clientul ramane nealocat. */
  const scoate = useMutation({
    mutationFn: async (ids: number[]) =>
      (await api.post('/agents/alocare', { agent, adauga: [], scoate: ids })).data,
    onSuccess: (r: { scosi: number }) => {
      toast.success(`${r.scosi} clienți scoși de la ${agent}.`)
      reset()
      void qc.invalidateQueries({ queryKey: ['alocare'] })
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message ?? 'Operația a eșuat.'),
  })

  const deblocheaza = useMutation({
    mutationFn: async (ids: number[]) =>
      (await api.post('/agents/alocare/deblocheaza', { ids })).data,
    onSuccess: () => {
      toast.success('Clientul revine sub alocarea din ExpertAccounts la următoarea sincronizare.')
      void qc.invalidateQueries({ queryKey: ['alocare'] })
    },
  })

  const unifica = useMutation({
    mutationFn: async (v: { sursaId: number; destinatieId: number }) =>
      (await api.post('/agents/alocare/unificare', v)).data,
    onSuccess: (r: { sters: string }) => {
      toast.success(`„${r.sters}" a fost unificat și șters.`)
      void qc.invalidateQueries({ queryKey: ['alocare'] })
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message ?? 'Unificarea a eșuat.'),
  })

  const filtrat = (lista: ClientAlocare[], termen: string) => {
    const t = termen.trim().toLowerCase()
    if (!t) return lista
    return lista.filter(
      (c) => c.name.toLowerCase().includes(t) || (c.cif ?? '').toLowerCase().includes(t),
    )
  }

  const alocati = useMemo(() => filtrat(data?.alocati ?? [], cautaAlocati), [data, cautaAlocati])
  const restul = useMemo(() => filtrat(data?.restul ?? [], cautaRestul), [data, cautaRestul])

  const bifa = (set: Set<number>, setter: (s: Set<number>) => void, id: number) => {
    const nou = new Set(set)
    if (nou.has(id)) nou.delete(id)
    else nou.add(id)
    setter(nou)
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Alocare clienți pe agenți</h1>
        <p className="mt-0.5 text-sm text-b4l-text-muted">
          Alegi agentul, bifezi clienții și salvezi. Alocarea făcută aici are prioritate față de
          portofoliile din ExpertAccounts — util când un agent pleacă și clienții lui rămân acolo pe
          numele lui.
        </p>
      </div>

      {unifEa && unifEa.unificate.length > 0 && (
        <Card
          title="Unificate în ExpertAccounts"
          description="Fișa lor nu mai e în EA — de obicei pentru că au fost unificate acolo cu alt partener. Unifică-i și aici, ca tranzacțiile și comenzile să treacă pe clientul care rămâne."
        >
          <div className="space-y-2">
            {unifEa.unificate.map((u) => (
              <div
                key={u.clientId}
                className="flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm"
              >
                <span className="font-medium">{u.denumire}</span>
                <span className="text-xs text-b4l-text-muted">
                  id {u.clientId} · {u.codFiscal ?? 'fără cod fiscal'} · lipsă din EA din{' '}
                  {new Date(u.lipsaDin).toLocaleDateString('ro-RO')}
                </span>
                <span className="ml-auto">
                  {u.propunere ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const p = u.propunere!
                        if (
                          window.confirm(
                            `Muți tot ce ține de „${u.denumire}" la „${p.denumire}" (${p.motiv}), apoi ștergi „${u.denumire}".

Nu se poate anula.`,
                          )
                        )
                          unifica.mutate({ sursaId: u.clientId, destinatieId: p.clientId })
                      }}
                    >
                      <ArrowLeftRight size={14} /> Unifică în „{u.propunere.denumire}” (
                      {u.propunere.motiv})
                    </Button>
                  ) : (
                    <span className="text-xs text-amber-900">
                      Niciun candidat sigur — verifică în EA în cine a fost unificat.
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {dup && dup.duplicate.length > 0 && (
        <Card
          title="Clienți duplicați"
          description="Același cod fiscal, denumiri diferite. Soldul lor se numără de două ori în tabloul de bord."
        >
          <div className="space-y-3">
            {dup.duplicate.map((d) => (
              <div key={d.cif} className="rounded border border-amber-200 bg-amber-50 p-3 text-sm">
                <div className="mb-2 text-xs text-amber-900">Cod fiscal {d.cif}</div>
                <div className="space-y-1">
                  {d.clienti.map((c) => (
                    <div key={c.id} className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{c.name}</span>
                      <span className="text-xs text-b4l-text-muted">
                        id {c.id} · {c.agentName ?? 'nealocat'}
                        {c.soldLei != null && ` · sold ${lei(c.soldLei)} lei`}
                      </span>
                      <span className="ml-auto flex gap-1">
                        {d.clienti
                          .filter((alt) => alt.id !== c.id)
                          .map((alt) => (
                            <Button
                              key={alt.id}
                              variant="ghost"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Muți tot ce ține de „${c.name}" la „${alt.name}", apoi ștergi „${c.name}".\n\nNu se poate anula.`,
                                  )
                                )
                                  unifica.mutate({ sursaId: c.id, destinatieId: alt.id })
                              }}
                            >
                              <ArrowLeftRight size={14} /> Mută în „{alt.name}”
                            </Button>
                          ))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-0.5 block text-[11px] text-b4l-text-muted">Agent</span>
            <select
              className="w-64 rounded border border-b4l-border bg-white px-2 py-2 text-sm"
              value={agent}
              onChange={(e) => {
                setAgent(e.target.value)
                reset()
              }}
            >
              <option value="">— alege un agent —</option>
              {(agenti?.agenti ?? []).map((a) => (
                <option key={a.name} value={a.name}>
                  {a.name} ({a.clienti})
                </option>
              ))}
            </select>
          </label>
          {selectatiAiLui.size > 0 && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="block">
                <span className="mb-0.5 block text-[11px] text-b4l-text-muted">
                  {selectatiAiLui.size} bifați la {agent} — mută la
                </span>
                <select
                  className="w-56 rounded border border-b4l-border bg-white px-2 py-2 text-sm"
                  value={mutaLa}
                  onChange={(e) => setMutaLa(e.target.value)}
                >
                  <option value="">— alege agentul —</option>
                  {(agenti?.agenti ?? [])
                    .filter((a) => a.name !== agent)
                    .map((a) => (
                      <option key={a.name} value={a.name}>
                        {a.name}
                      </option>
                    ))}
                </select>
              </label>
              <Button
                disabled={!mutaLa}
                loading={muta.isPending}
                onClick={() => muta.mutate({ catre: mutaLa, ids: [...selectatiAiLui] })}
              >
                <ArrowLeftRight size={16} /> Mută
              </Button>
              {/* Separat de mutare: „nu mai e al nimanui" e alta decizie, nu o mutare fara destinatie. */}
              <Button
                variant="secondary"
                loading={scoate.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `Scoți ${selectatiAiLui.size} clienți de la ${agent} fără să-i dai altcuiva?

Rămân nealocați.`,
                    )
                  )
                    scoate.mutate([...selectatiAiLui])
                }}
              >
                Scoate, fără agent
              </Button>
            </div>
          )}

          {selectatiCeilalti.size > 0 && (
            <div className="flex items-end gap-2">
              <Button
                loading={muta.isPending}
                onClick={() => muta.mutate({ catre: agent, ids: [...selectatiCeilalti] })}
              >
                <ArrowLeftRight size={16} /> Adu {selectatiCeilalti.size} la {agent}
              </Button>
            </div>
          )}

          {(selectatiAiLui.size > 0 || selectatiCeilalti.size > 0) && (
            <Button variant="ghost" onClick={reset}>
              Renunță
            </Button>
          )}
        </div>
      </Card>

      {!agent ? (
        <Card>
          <p className="flex items-center justify-center gap-2 py-10 text-sm text-b4l-text-muted">
            <Users size={16} /> Alege un agent ca să vezi clienții.
          </p>
        </Card>
      ) : isLoading ? (
        <Card>
          <p className="py-10 text-center text-sm text-b4l-text-muted">Se încarcă…</p>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Lista
            titlu={`Clienții lui ${agent}`}
            subtitlu={`${data?.alocati.length ?? 0} clienți · bifează, apoi mută-i la alt agent`}
            clienti={alocati}
            cauta={cautaAlocati}
            setCauta={setCautaAlocati}
            bifate={selectatiAiLui}
            onBifa={(id) => bifa(selectatiAiLui, setSelectatiAiLui, id)}
            aratAgent={false}
            onDeblocheaza={(id) => deblocheaza.mutate([id])}
          />
          <Lista
            titlu="Ceilalți clienți"
            subtitlu={`${data?.restul.length ?? 0} clienți · bifează ca să-i aduci la ${agent}`}
            clienti={restul}
            cauta={cautaRestul}
            setCauta={setCautaRestul}
            bifate={selectatiCeilalti}
            onBifa={(id) => bifa(selectatiCeilalti, setSelectatiCeilalti, id)}
            aratAgent
            onDeblocheaza={(id) => deblocheaza.mutate([id])}
          />
        </div>
      )}
    </div>
  )
}

function Lista({
  titlu,
  subtitlu,
  clienti,
  cauta,
  setCauta,
  bifate,
  onBifa,
  aratAgent,
  onDeblocheaza,
}: {
  titlu: string
  subtitlu: string
  clienti: ClientAlocare[]
  cauta: string
  setCauta: (v: string) => void
  bifate: Set<number>
  onBifa: (id: number) => void
  aratAgent: boolean
  onDeblocheaza: (id: number) => void
}) {
  return (
    <Card title={titlu} description={subtitlu}>
      <div className="relative mb-2">
        <Search
          size={15}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-b4l-text-muted"
        />
        <input
          className="w-full rounded border border-b4l-border py-1.5 pl-7 pr-2 text-sm"
          placeholder="Caută după nume sau cod fiscal"
          value={cauta}
          onChange={(e) => setCauta(e.target.value)}
        />
      </div>
      <div className="max-h-[28rem] divide-y divide-b4l-border/60 overflow-y-auto">
        {clienti.length === 0 ? (
          <p className="py-8 text-center text-sm text-b4l-text-muted">Niciun client.</p>
        ) : (
          clienti.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-center gap-2 py-1.5 text-sm hover:bg-b4l-bg"
            >
              <input
                type="checkbox"
                checked={bifate.has(c.id)}
                onChange={() => onBifa(c.id)}
                className="shrink-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{c.name}</span>
                <span className="block truncate text-xs text-b4l-text-muted">
                  {c.cif ?? 'fără CIF'}
                  {aratAgent && ` · ${c.agentName ?? 'nealocat'}`}
                  {c.soldLei != null && c.soldLei !== 0 && ` · sold ${lei(c.soldLei)} lei`}
                </span>
              </span>
              {c.agentLocked && (
                <button
                  type="button"
                  title="Alocat manual — sincronizarea din EA nu-l schimbă. Apasă ca să revii la ce spune EA."
                  onClick={(e) => {
                    e.preventDefault()
                    onDeblocheaza(c.id)
                  }}
                  className="shrink-0 text-b4l-text-muted hover:text-b4l-text"
                >
                  <Lock size={14} />
                </button>
              )}
              {!c.agentLocked && <Unlock size={14} className="shrink-0 text-b4l-border" />}
            </label>
          ))
        )}
      </div>
    </Card>
  )
}
